import apiClient from './client';
import type { UserStats, UserProfile, UserProgress, CertificateInfo } from '../types/auth';

// Re-export types so existing imports from 'api/auth' still work
export type { UserStats, UserProfile, UserProgress, CertificateInfo };

export async function fetchUserStats(): Promise<UserStats> {
  const { data } = await apiClient.get<UserStats>('/api/auth/me/stats');
  return data;
}

export async function fetchUserProfile(): Promise<UserProfile> {
  const { data } = await apiClient.get<UserProfile>('/api/auth/me');
  return data;
}

export async function updateProfile(payload: {
  display_name?: string | null;
  username: string;
  email: string;
  bio?: string | null;
  exam_date?: string | null;
  exam_center?: string | null;
}): Promise<UserProfile> {
  const { data } = await apiClient.patch<UserProfile>('/api/auth/me', payload);
  return data;
}

export async function uploadAvatar(file: File): Promise<UserProfile> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<UserProfile>('/api/auth/me/avatar', form);
  return data;
}

export async function changePassword(payload: {
  current_password: string;
  new_password: string;
}): Promise<void> {
  await apiClient.post('/api/auth/me/change-password', payload);
}

/** Purge the current user's session history (study/exam sessions + answers). */
export async function resetStats(): Promise<void> {
  await apiClient.post('/api/auth/me/reset');
}

/** Purge the current user's learning advancement (spaced-repetition + streaks). */
export async function resetProgress(): Promise<void> {
  await apiClient.post('/api/auth/me/reset-progress');
}

export async function fetchUserProgress(): Promise<UserProgress> {
  const { data } = await apiClient.get<UserProgress>('/api/auth/me/progress');
  return data;
}

export async function completeOnboarding(payload: {
  display_name?: string;
  self_assessed_level?: string;
  onboarding_completed: boolean;
}): Promise<UserProfile> {
  const { data } = await apiClient.patch<UserProfile>('/api/auth/me', payload);
  return data;
}

export async function downloadMyData(): Promise<Blob> {
  const { data } = await apiClient.get('/api/auth/my-data', { responseType: 'blob' });
  return data;
}

export async function deleteMyAccount(password: string): Promise<void> {
  await apiClient.post('/api/auth/delete-account', { password });
}

export async function fetchMyCertificates(): Promise<CertificateInfo[]> {
  const { data } = await apiClient.get<CertificateInfo[]>('/api/auth/me/certificates');
  return data;
}

export async function requestPasswordReset(email: string): Promise<{ message: string; reset_url?: string }> {
  const { data } = await apiClient.post<{ message: string; reset_url?: string }>('/api/auth/forgot-password', { email });
  return data;
}

export async function resetPasswordWithToken(token: string, newPassword: string): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>('/api/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return data;
}

/** Login and return token + user. */
export async function loginUser(
  email: string,
  password: string,
): Promise<{ access_token: string; user: UserProfile }> {
  const { data } = await apiClient.post<{ access_token: string; user: UserProfile }>(
    '/api/auth/login',
    { email, password },
  );
  return data;
}

/** Register a new user. */
export async function registerUser(
  username: string,
  email: string,
  password: string,
  consent: boolean,
  captchaToken: string,
  acceptTerms: boolean,
  termsVersion: string,
): Promise<void> {
  await apiClient.post('/api/auth/register', {
    username,
    email,
    password,
    consent,
    captcha_token: captchaToken,
    accept_terms: acceptTerms,
    terms_version: termsVersion,
  });
}

/** Verify email with a 6-digit code. Returns token + user on success. */
export async function verifyEmail(
  email: string,
  code: string,
): Promise<{ access_token: string; user: UserProfile }> {
  const { data } = await apiClient.post<{ access_token: string; user: UserProfile }>(
    '/api/auth/verify-email',
    { email, code },
  );
  return data;
}

/** Resend the email verification code. */
export async function resendVerification(
  email: string,
): Promise<{ message: string }> {
  const { data } = await apiClient.post<{ message: string }>(
    '/api/auth/resend-verification',
    { email },
  );
  return data;
}

/** Record explicit GDPR consent for a new OAuth user. */
export async function submitOAuthConsent(): Promise<{ message: string; user: UserProfile }> {
  const { data } = await apiClient.post<{ message: string; user: UserProfile }>(
    '/api/auth/oauth/consent',
  );
  return data;
}

/** Delete an OAuth-only account (no password required, uses confirmation string). */
export async function deleteOAuthAccount(): Promise<void> {
  await apiClient.post('/api/auth/delete-account', { password: 'DELETE_MY_ACCOUNT' });
}

/** Get the OAuth authorization URL for a given provider. */
export async function getOAuthAuthorizeUrl(
  provider: string,
): Promise<{ authorize_url: string }> {
  const { data } = await apiClient.get<{ authorize_url: string }>(
    '/api/auth/oauth/authorize-url',
    { params: { provider } },
  );
  return data;
}

/** Exchange OAuth callback code for access token. */
export async function exchangeOAuthCode(
  provider: string,
  code: string,
  state: string | null,
): Promise<{ access_token: string; user: UserProfile }> {
  const { data } = await apiClient.post<{ access_token: string; user: UserProfile }>(
    '/api/auth/oauth/callback',
    { provider, code, state },
  );
  return data;
}
